import Phaser from "phaser";
import type { CardStyleToken } from "./tokens.js";

/**
 * 표준 NineSlice 카드 — `CardStyleToken` 토큰 기반.
 *
 * `setSelected(bool)` 호출 시 destroy/recreate 없이 `setTexture` + `setAlpha` 부분 갱신만 수행 (출시 스펙 정합).
 *
 * 사용 예:
 * ```ts
 * const card = new NineSliceCard(scene, x, y, {
 *   style: CARD_STYLES.warn,
 *   width: 600,
 *   height: 120,
 *   selected: false,
 * });
 * this.add(card);
 * card.setSelected(true);
 * ```
 */
export class NineSliceCard extends Phaser.GameObjects.NineSlice {
  private readonly cardStyle: CardStyleToken;
  private selected: boolean;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    options: {
      readonly style: CardStyleToken;
      readonly width: number;
      readonly height: number;
      /** 초기 selected 상태 (default false). */
      readonly selected?: boolean;
      readonly originX?: number;
      readonly originY?: number;
    },
  ) {
    const { style, width, height } = options;
    const initialSelected = options.selected ?? false;
    super(
      scene,
      x,
      y,
      initialSelected ? style.selectedKey : style.normalKey,
      undefined,
      width,
      height,
      style.border.left,
      style.border.right,
      style.border.top,
      style.border.bottom,
    );
    this.setOrigin(options.originX ?? 0, options.originY ?? 0);
    this.cardStyle = style;
    this.selected = initialSelected;
    this.setAlpha(initialSelected ? style.alphaSelected : style.alphaNormal);
  }

  /** 선택 상태 토글 — destroy/recreate 0건. setTexture + setAlpha 부분 갱신만. */
  setSelected(selected: boolean): void {
    if (this.selected === selected) return;
    this.selected = selected;
    this.setTexture(selected ? this.cardStyle.selectedKey : this.cardStyle.normalKey);
    this.setAlpha(selected ? this.cardStyle.alphaSelected : this.cardStyle.alphaNormal);
  }

  /** 현재 selected 상태 반환. setSelected 호출 결과를 보존. */
  isSelected(): boolean {
    return this.selected;
  }
}
